"use client";

import React, { useRef, useEffect } from "react";
import { Link } from "@/components/common/Link";
import { SectionHeading } from "@/components/cosmic/SectionHeading";
import { Reveal } from "@/components/cosmic/Reveal";
import { useReducedMotion } from "@/components/cosmic/useReducedMotion";
import { ArrowRight, Binary, Cpu, Compass, Layers } from "lucide-react";
export default function SimulationTheory() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const reducedMotion = useReducedMotion();

  // Subtle perspective grid + drifting coordinate points
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    let animId: number | null = null;
    let width = canvas.width = canvas.clientWidth || 600;
    let height = canvas.height = canvas.clientHeight || 280;
    const onResize = () => {
      if (!canvas) return;
      width = canvas.width = canvas.clientWidth || 600;
      height = canvas.height = canvas.clientHeight || 280;
    };
    window.addEventListener("resize", onResize);

    // Coordinate particles
    const particles = Array.from({
      length: 24
    }, (_, i) => ({
      x: i * 37 % width,
      y: i * 29 % height,
      speed: 0.15 + i % 3 * 0.1,
      size: 1 + i % 2,
      alpha: 0.2 + i % 4 * 0.15
    }));
    let isVisible = true;
    let observer: IntersectionObserver | null = null;
    if (typeof IntersectionObserver !== "undefined") {
      observer = new IntersectionObserver(entries => {
        isVisible = entries[0]?.isIntersecting ?? true;
        if (isVisible && !reducedMotion && !animId) {
          animId = requestAnimationFrame(render);
        }
      }, {
        threshold: 0.05
      });
      observer.observe(canvas);
    }
    let t = 0;
    const render = () => {
      if (!isVisible) {
        animId = null;
        return;
      }
      ctx.clearRect(0, 0, width, height);

      // Deep perspective grid lines converging toward horizon
      const horizon = height * 0.45;
      const vanishingX = width * 0.5;
      ctx.save();
      ctx.strokeStyle = "rgba(0, 229, 255, 0.08)";
      ctx.lineWidth = 1;

      // Radial perspective rays
      const rays = 12;
      for (let i = 0; i <= rays; i++) {
        const bottomX = i / rays * width;
        ctx.beginPath();
        ctx.moveTo(vanishingX, horizon);
        ctx.lineTo(bottomX, height);
        ctx.stroke();
      }

      // Horizontal lines with exponential perspective compression
      for (let j = 1; j <= 6; j++) {
        const factor = Math.pow(j / 6, 2);
        const y = horizon + factor * (height - horizon);
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(width, y);
        ctx.stroke();
      }
      ctx.restore();

      // Drifting coordinate particles
      if (!reducedMotion) {
        t += 0.01;
        particles.forEach(p => {
          p.y -= p.speed;
          if (p.y < 0) p.y = height;
          ctx.fillStyle = `rgba(0, 229, 255, ${p.alpha})`;
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
          ctx.fill();
        });
        animId = requestAnimationFrame(render);
      } else {
        // Static frame under reduced motion
        particles.forEach(p => {
          ctx.fillStyle = `rgba(0, 229, 255, ${p.alpha * 0.6})`;
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
          ctx.fill();
        });
      }
    };
    render();
    return () => {
      if (animId) cancelAnimationFrame(animId);
      if (observer) observer.disconnect();
      window.removeEventListener("resize", onResize);
    };
  }, [reducedMotion]);
  const features = [{
    icon: Binary,
    title: "10 Interactive Missions",
    desc: "From sensory limits to Planck quantization and the Bostrom trilemma."
  }, {
    icon: Cpu,
    title: "Build a Toy Universe",
    desc: "Balance grid resolution, physical laws, and simulation limits."
  }, {
    icon: Compass,
    title: "Rigorously Educational",
    desc: "Strictly separates real physics from philosophical speculation."
  }, {
    icon: Layers,
    title: "Free, Browser-Only",
    desc: "No accounts, no paywalls, completely deterministic simulations."
  }];
  return <section id="simulation-theory-preview" data-nav="dark" className="relative py-16 sm:py-20 lg:py-28 w-full max-w-full overflow-x-clip text-foreground scroll-mt-12">
      <div className="relative z-10 max-w-[76rem] w-full mx-auto px-4 sm:px-6">
        {/* Hairline section divider */}
        <div className="ql-divider mb-14 sm:mb-16" aria-hidden="true" />

        <Reveal>
          <SectionHeading eyebrow="SIMULATION THEORY" title="Question the Nature of Reality." description="An interactive scientific and philosophical investigation of perception, quantum mechanics, computation, and Bostrom's trilemma. Test ideas through hands-on models and examine what modern empirical physics does and does not know—without ever claiming a simulation is proven." align="center" />
        </Reveal>

        {/* Ambient perspective grid motif box with telemetry border */}
        <Reveal delay={150} className="mt-10 sm:mt-12">
          <div className="relative rounded-2xl border border-border bg-card/60 backdrop-blur-xl p-6 sm:p-8 lg:p-10 overflow-hidden shadow-[0_16px_40px_-10px_rgba(0,0,0,0.85)]">
            {/* Top specular hairline accent */}
            <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary/50 to-transparent" />

            {/* Background canvas perspective grid */}
            <div className="absolute inset-0 pointer-events-none opacity-60">
              <canvas ref={canvasRef} className="w-full h-full block" aria-hidden="true" />
            </div>

            <div className="relative z-10">
              {/* Feature Grid: 4 compact chips */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
                {features.map(feat => {
                const Icon = feat.icon;
                return <div key={feat.title} className="p-4 sm:p-5 rounded-xl border border-border/70 bg-card/80 backdrop-blur-md transition-all duration-300 hover:border-primary/60 hover:shadow-[0_0_20px_rgba(0,229,255,0.15)] flex flex-col justify-between">
                      <div>
                        <div className="size-9 rounded-lg bg-primary/10 border border-primary/30 flex items-center justify-center text-primary mb-3">
                          <Icon className="size-4" />
                        </div>
                        <h3 className="font-serif text-sm sm:text-base font-bold text-foreground mb-1.5 leading-snug">
                          {feat.title}
                        </h3>
                        <p className="font-sans text-xs text-muted-foreground leading-relaxed">
                          {feat.desc}
                        </p>
                      </div>
                    </div>;
              })}
              </div>

              {/* Primary Action Button linking to /SimulationTheoryPage */}
              <div className="mt-8 sm:mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link to="/SimulationTheoryPage" className="ql-btn min-h-[48px] px-8 py-3.5 text-sm font-serif uppercase tracking-wider justify-center shadow-[0_0_24px_rgba(0,229,255,0.35)]">
                  <span>Enter the investigation</span>
                  <ArrowRight className="size-4 shrink-0" />
                </Link>
                <span className="font-mono text-xs text-muted-foreground uppercase tracking-wider">
                  // Mission 01: The Reality Test
                </span>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>;
}